'use client';

import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { useEffect, useState } from 'react';
import { UpgradeBanner, useV1ToV2MigrationTarget } from '@/components/common/V1ToV2Migration';
import OperationModeButton from '@/components/SimpleActionPanel/OperationModeButton/OperationModeButton';
import { useWeb3Context } from '@/contexts/Web3Context';
import { InterfaceOperation } from '@/operations/operations.types';
import { useVaultV1Position } from '../hooks/useVaultV1Position';
import { toVaultV1ActionPanelData, type VaultV1ActionPanelOutput } from './vaultV1ActionPanelData';
import { VaultV1OperationDeposit, VaultV1OperationWithdraw } from './VaultV1OperationPanel';
interface VaultSimpleActionPanelProps {
  vaultAddress: Address;
  chainId: ChainId;
  /** Null when the server read failed: the panel still renders, without vault data. */
  actionPanel: VaultV1ActionPanelOutput | null;
}
const VaultSimpleActionPanel = ({
  vaultAddress,
  chainId,
  actionPanel
}: VaultSimpleActionPanelProps) => {
  const vaultData = actionPanel ? toVaultV1ActionPanelData(actionPanel, chainId) : null;
  const {
    targetChainId
  } = useWeb3Context();
  const [type, setType] = useState(InterfaceOperation.InterfaceOperationType.metaMorphoDeposit);
  const {
    userVaultShares
  } = useVaultV1Position({
    vaultAddress,
    chainId: vaultData ? chainId : undefined
  });
  const migrationTarget = useV1ToV2MigrationTarget(vaultAddress, targetChainId);

  // The holding gates the Withdraw tab and the upgrade nudge.
  const hasHolding = isDefined(userVaultShares) && userVaultShares > 0n;
  useEffect(() => {
    if (!hasHolding) {
      setType(InterfaceOperation.InterfaceOperationType.metaMorphoDeposit);
    }
  }, [hasHolding]);
  return <div className="gap-sm flex basis-[100%] flex-col" data-testid="vault-action-panel">
      {hasHolding && migrationTarget && <UpgradeBanner migrationTarget={migrationTarget} />}
      <div className="gap-2xs flex items-center justify-stretch">
        <OperationModeButton onClick={() => setType(InterfaceOperation.InterfaceOperationType.metaMorphoDeposit)} label="Deposit" isActive={type === InterfaceOperation.InterfaceOperationType.metaMorphoDeposit} />
        <OperationModeButton onClick={() => setType(InterfaceOperation.InterfaceOperationType.metaMorphoWithdraw)} label="Withdraw" isActive={type === InterfaceOperation.InterfaceOperationType.metaMorphoWithdraw} disabled={!hasHolding} />
      </div>
      {type === InterfaceOperation.InterfaceOperationType.metaMorphoDeposit && <VaultV1OperationDeposit vaultAddress={vaultAddress} vaultData={vaultData} />}
      {type === InterfaceOperation.InterfaceOperationType.metaMorphoWithdraw && <VaultV1OperationWithdraw vaultAddress={vaultAddress} vaultData={vaultData} />}
    </div>;
};
export default VaultSimpleActionPanel;